import { isEmpty, isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Button from '@/components/Button'
import Form, { Checkbox } from '@/components/Form'
import Page from '@/components/Page'
import { setNewUser } from '@/pages/SpaceMembersPage/actions'
import { getIsNewUser, getUserInCurrentSpaceToEdit } from '@/pages/SpaceMembersPage/selectors'
import { fetchRoles, fetchUser } from '@/store/actions'
import { ADD_MEMBER_TO_SPACE, FETCH_USER } from '@/store/actionTypes'
import { getCurrentSpace, getCurrentUser, getSpaceRoles, isRequestPending } from '@/store/selectors'

import { addUserToSpace, selectEditSpaceMember, updateSpaceRoles } from './actions'
import { UPDATE_SPACE_ROLE } from './actionTypes'
import messages from './messages'
import UserSearch from './UserSearch'

import './index.scss'

import { useHistory, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'

import filterSpaceRoles from '@/components/RolesContainer/filterSpaceRoles'
import useFeatures from '@/components/useFeatures'

const StyledCheckboxInput = styled(Checkbox.Input)`
  max-width: 550px;
`

function SpaceMemberEditPage({ actions, space, isLoading, isUpdating, isNew, user, loggedInUser, spaceRoles }: any) {
  const history = useHistory()
  const location = useLocation()
  const params = useParams<{ userId?: string }>()

  const features = useFeatures()

  const [isNewState, setIsNew] = useState(isNew)
  const [userState, setUser] = useState(user ? { ...user } : {})

  const isUpdatingRef = useRef<boolean>(isUpdating)
  const userRef = useRef<any>({})

  useEffect(() => {
    actions.fetchRoles()
    if (params.userId) {
      actions.selectEditSpaceMember(params.userId)
      actions.fetchUser(params.userId)
    } else if (location.pathname.includes('/new')) {
      actions.setNewUser(true)
      setIsNew(true)
      setUser({ user: {} })
    }
  }, [actions, params.userId, location.pathname])

  useEffect(() => {
    if (isUpdatingRef.current && !isUpdating) {
      goBack()
    }

    if (!isEqual(user, userRef.current) && !isUpdating && !isNewState) {
      setUser(user)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdating, userState, isNewState, user])

  const stripPath = (path: any, count: any) => {
    for (let i = 0; i < count; i++) {
      path = path.slice(0, Math.max(0, path.lastIndexOf('/')))
    }

    return path
  }

  const goBack = () => {
    const targetPath = stripPath(location.pathname, 1)
    history.push(targetPath)
  }

  const selectUser = (user: any) => {
    setUser({ user: user })
  }

  const updateUserInSpace = async (values: any) => {
    isUpdatingRef.current = true
    if (isNewState) {
      actions.addUserToSpace(space.id, {
        id: values.userSearch,
        rolesInSpace: values.rolesInSpace
      })
    } else {
      if (userState.id === loggedInUser.id) {
        await actions.updateSpaceRoles(space.id, userState.id, values.rolesInSpace)
      } else {
        actions.updateSpaceRoles(space.id, userState.id, values.rolesInSpace)
      }
    }
  }

  spaceRoles = filterSpaceRoles(spaceRoles, features, true)

  return (
    <Page className="space-member-edit-user-wrapper" isLoading={isLoading}>
      <Page.Header
        title={
          isNewState ? (
            <FormattedMessage {...messages.addUserTitle} />
          ) : (
            <FormattedMessage {...messages.editUserTitle} />
          )
        }
        goBack={goBack}
        backNavTitle={<FormattedMessage {...messages.back} />}
      />
      {!isEmpty(userState) && (
        <Form
          actionMessage={
            loggedInUser.id === userState.id ? (
              <FormattedMessage {...messages.updatingLoggedInUser} />
            ) : (
              <FormattedMessage {...messages.updatingMember} />
            )
          }
          enableReinitialize
          initialValues={{
            givenName: userState.givenName || '',
            familyName: userState.familyName || '',
            displayName: userState.displayName || '',
            externalId: userState.externalId || '',
            email: userState.email || '',
            rolesInSpace: userState.rolesInSpace ? userState.rolesInSpace.map((role: any) => role.id) : []
          }}
          isActionInProgress={isUpdating}
          onSubmit={updateUserInSpace}
          onReset={goBack}
          buttons={
            <>
              <Button color="secondary" type="button" onClick={goBack}>
                <FormattedMessage {...messages.cancel} />
              </Button>
              <Button color="primary" type="submit" id="update" disabled={isNewState && isEmpty(userState)}>
                <FormattedMessage {...(isNewState ? messages.add : messages.update)} />
              </Button>
            </>
          }
          title={
            isNewState ? (
              ''
            ) : (
              <>
                <h1>{userState.displayName || userState.givenName + ' ' + userState.familyName}</h1>
                <p className="pb-2 font-normal text-zinc-500">{userState.email}</p>
              </>
            )
          }
        >
          {!!isNewState && <UserSearch onOptionSelected={selectUser} />}

          <>
            {spaceRoles.map((spaceRole: any) => (
              <StyledCheckboxInput
                key={spaceRole.name}
                description={spaceRole.description}
                label={<span>{spaceRole.displayName}</span>}
                name="rolesInSpace"
                id={spaceRole.id}
                value={spaceRole.id}
              />
            ))}
            {loggedInUser.id === userState.id && (
              <div className="warning logout-after-update">
                <FormattedMessage {...messages.logOutAfterUpdate} />
              </div>
            )}
          </>
        </Form>
      )}
    </Page>
  )
}

SpaceMemberEditPage.displayName = 'SpaceMemberEditPage'

export { /** @deprecated */ SpaceMemberEditPage }

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      selectEditSpaceMember,
      fetchUser,
      fetchRoles,
      addUserToSpace,
      updateSpaceRoles,
      setNewUser
    },
    dispatch
  )
})

const mapStateToProps = (state: any) => ({
  space: getCurrentSpace(state),
  isLoading: isRequestPending(state, FETCH_USER),
  isUpdating: isRequestPending(state, [UPDATE_SPACE_ROLE, ADD_MEMBER_TO_SPACE]),
  isNew: getIsNewUser(state),
  user: getUserInCurrentSpaceToEdit(state),
  loggedInUser: getCurrentUser(state),
  spaceRoles: getSpaceRoles(state)
})

SpaceMemberEditPage.propTypes = {
  actions: PropTypes.shape({
    addUserToSpace: PropTypes.func.isRequired,
    fetchUser: PropTypes.func.isRequired,
    fetchRoles: PropTypes.func.isRequired,
    selectEditSpaceMember: PropTypes.func.isRequired,
    setNewUser: PropTypes.func.isRequired,
    updateSpaceRoles: PropTypes.func.isRequired
  }).isRequired,
  space: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isNew: PropTypes.bool,
  user: PropTypes.object,
  loggedInUser: PropTypes.object.isRequired,
  spaceRoles: PropTypes.arrayOf(PropTypes.object).isRequired
}

export default connect(mapStateToProps, mapDispatchToProps)(SpaceMemberEditPage)
